import { getValueByStorePath } from '../helpers';

export const _getDataField = (storeData, fieldName) => {
    if (storeData && fieldName) {
        return getValueByStorePath(fieldName, storeData);
    }
    return undefined;
};

const getSelectors = storeName => {
    const getStore = state => (storeName ? state && state[storeName] : state) || {};
    const getData = state => getStore(state).data || {};
    const getMetadata = state => getStore(state).metadata;
    const getFormId = state => getStore(state).formId;
    const getStep = state => getStore(state).step;
    const isFocused = state => getStore(state).isFocused;
    const isLoading = state => getStore(state).loading;
    const isStepValidationLoading = state => getStore(state).stepValidationLoading;
    const getDataError = state => getStore(state).dataError;
    const getSuccessRedirectLink = state => getStore(state).successRedirectLink;
    const getFormElement = state => getStore(state).formElement || {};
    const getFormElementId = state => getStore(state).formElementId || {};
    const getFailRedirectLink = state => getStore(state).failRedirectLink;
    const getErrorsText = state => getStore(state).errorsText;
    const getSubmitUrl = state => getStore(state).submitUrl;
    const getSubmitMethod = state => getStore(state).submitMethod;

    const hasValidationError = state =>
        Object.values(getData(state)).some(field => field.error && field.error.length);

    const getDataField = (state, fieldName) => {
        return _getDataField(getData(state), fieldName);
    };

    const getCurrentFocusedField = state =>
        Object.values(getData(state)).find((field = {}) => field.focused) || {};

    const getDataFieldProperty = (state, fieldName, propertyName) =>
        (getDataField(state, fieldName) || {})[propertyName];
    const getDataFieldValue = (state, fieldName) => {
        const dataField = getDataField(state, fieldName) || {};
        // difference between 'empty string' and 'undefined' is used in validation
        return dataField.value || dataField.defaultValue || dataField.value;
    };
    const getDataFieldLinks = (state, fieldName) =>
        (getDataField(state, fieldName) || {}).links || [];
    const getDataFieldLink = (state, fieldName, linkName) =>
        getDataFieldLinks(state, fieldName).find(({ link }) => link === linkName);

    const isDataFieldRequired = (state, fieldName) =>
        !!(getDataField(state, fieldName) || {}).required;
    const isDataFieldHidden = (state, fieldName) => {
        const dataField = getDataField(state, fieldName) || {};
        return dataField.type === 'HIDDEN';
    };
    const isDataFieldDisabled = (state, fieldName) =>
        !!(getDataField(state, fieldName) || {}).disabled;
    const getDataFieldError = (state, fieldName) => (getDataField(state, fieldName) || {}).error;
    const getDataFieldTypeError = (state, fieldName) =>
        (getDataField(state, fieldName) || {}).typeError;
    const isDataFieldFocused = (state, fieldName) =>
        (getDataField(state, fieldName) || {}).focused || false;
    const isDataFieldTouched = (state, fieldName) =>
        (getDataField(state, fieldName) || {}).touched || false;
    const getDataFieldConstraints = (state, fieldName) =>
        (getDataField(state, fieldName) || {}).constraints || [];
    const getDataFieldConstraintByType = (state, fieldName, constraintType) =>
        getDataFieldConstraints(state, fieldName).find(({ type }) => type === constraintType) || {};
    const getDataFieldConstraintAttribute = (state, fieldName, constraintType, attribute) =>
        getDataFieldConstraintByType(state, fieldName, constraintType)[attribute] || {};
    const getDataFieldConstraintByIndex = (state, fieldName, idx) =>
        (getDataFieldConstraints(state, fieldName) || [])[idx];

    const getDataFieldInitialConstraints = (state, fieldName) =>
        (getDataField(state, fieldName) || {}).initialConstraints || [];
    const getDataFieldInitialConstraintByType = (state, fieldName, constraintType) =>
        getDataFieldInitialConstraints(state, fieldName).find(
            ({ type }) => type === constraintType,
        ) || {};

    const isValidated = (state, fieldName) => (getDataField(state, fieldName) || {}).validated;

    const getGlobalMessage = state => getStore(state).globalMessage || {};

    const getAddressSuggestions = state => {
        const form = getStore(state);
        const addressSuggestions = form.addressSuggestions || {};
        return addressSuggestions.suggestions || [];
    };

    const getAddressSuggestionsSourceValue = state => {
        const form = getStore(state);
        const addressSuggestions = form.addressSuggestions || {};
        return addressSuggestions.sourceValue || '';
    };

    const isAddressValid = state => !!getStore(state).isAddressValid;
    const isNestedAddressValid = state => !!getStore(state).isNestedAddressValid;

    const getHostDialog = state => getStore(state).hostDialog || '';
    const getLoyaltyFlag = state => getStore(state).isLoyaltyFlag || false;

    return {
        getStore,
        getData,
        getMetadata,
        getFormId,
        getStep,
        getDataError,
        isFocused,
        isLoading,
        isStepValidationLoading,
        getDataField,
        getDataFieldProperty,
        getDataFieldValue,
        getDataFieldLinks,
        getDataFieldLink,
        getDataFieldError,
        getDataFieldTypeError,
        isDataFieldFocused,
        isDataFieldTouched,
        getDataFieldConstraints,
        getDataFieldConstraintByType,
        getDataFieldConstraintAttribute,
        getDataFieldConstraintByIndex,
        getDataFieldInitialConstraints,
        getDataFieldInitialConstraintByType,
        getSuccessRedirectLink,
        getFailRedirectLink,
        getErrorsText,
        isDataFieldRequired,
        isDataFieldHidden,
        getSubmitUrl,
        getSubmitMethod,
        getAddressSuggestions,
        getAddressSuggestionsSourceValue,
        getGlobalMessage,
        isAddressValid,
        isNestedAddressValid,
        getCurrentFocusedField,
        hasValidationError,
        isValidated,
        getHostDialog,
        isDataFieldDisabled,
        getFormElement,
        getFormElementId,
        getLoyaltyFlag,
    };
};

export default getSelectors;
